import queryString from 'query-string';
import { format } from 'date-fns';
import { defineMessages } from 'react-intl';
import { ccIntl } from 'helpers/intl-helper';

const { formatMessage } = ccIntl;
const messages = defineMessages({
  nameEmpty: {
    id: 'Helpers.strings.nameEmpty',
    defaultMessage: 'Name cannot be empty',
    description: 'error message',
  },
  invalidFilename: {
    id: 'Helpers.strings.invalidFilename',
    defaultMessage: "Name should not start with a '.'",
    description: 'error message',
  },
});
const nameEmpty = formatMessage(messages.nameEmpty);
const invalidFilename = formatMessage(messages.invalidFilename);

const caseInsensitiveContains = (str, query) => {
  if (typeof str !== 'string' || typeof query !== 'string') {
    return false;
  }
  return str.toLowerCase().indexOf(query.toLowerCase()) !== -1;
};

const statusInvalid = (invalidReason) => ({
  isValid: false,
  invalidReason,
});

const statusValid = () => ({
  isValid: true,
  invalidReason: '',
});

const INVALID_FILENAME_CHAR_REGEXP = /[^A-Za-z0-9 ',_\-\.\[\]\(\)]/g; // eslint-disable-line no-useless-escape

const getFilenameValidity = (filename) => {
  if (typeof filename !== 'string' || filename.length === 0) {
    return statusInvalid(nameEmpty);
  }

  // Don't start with '.', since that would make the file hidden on mac/linux.
  if (filename.match(/^\./) !== null) {
    return statusInvalid(invalidFilename);
  }

  return statusValid();
};

const stripInvalidFilenameChars = (s) =>
  s.replace(INVALID_FILENAME_CHAR_REGEXP, '');

const formatDownloadFilenameNoExt = ({
  projectName = '',
  tagValues = [],
  date = Date.now(),
  identifier = '',
}) => {
  const filenamePartTags = tagValues
    .map(stripInvalidFilenameChars)
    .map((x) => x.trim())
    .filter((x) => !!x)
    .join(' ');

  return [
    filenamePartTags,
    stripInvalidFilenameChars(projectName).trim(),
    format(date, 'MMM dd y hh_mma'),
    stripInvalidFilenameChars(identifier).trim(),
  ]
    .filter((x) => !!x)
    .join('-');
};

const formatBytes = (bytes) => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) {
    return `${Math.round(kilobytes * 10) / 10} KB`;
  }

  const megabytes = kilobytes / 1024;
  return `${Math.round(megabytes * 10) / 10} MB`;
};

const formatURLFacebookShare = ({ url, title }) => {
  const q = queryString.stringify({ u: url, title });
  return `http://www.facebook.com/sharer/sharer.php?${q}`;
};

const formatURLTwitterShare = ({ url, text }) => {
  const q = queryString.stringify({ url, text });
  return `https://twitter.com/intent/tweet?${q}`;
};

const formatURLMailto = ({ subject, bodyLines }) => {
  const q = queryString.stringify({ subject, body: bodyLines.join('\r\n') });
  return `mailto:?${q}`;
};

const initialsFromName = (name) =>
  name
    .split(' ')
    .map((nameComponent) => nameComponent.charAt(0))
    .map((initial) => initial.toUpperCase())
    .join('');

const getDisplayNameFromUser = (user, fallback = '') => {
  return user.name || user.emailAddress || user.phoneNumber || fallback;
};

const truncateText = (text, maxLength = 20) => {
  if (text.length > maxLength) {
    return `${text.substring(0, maxLength - 1)}…`;
  }

  return text;
};

export {
  caseInsensitiveContains,
  getFilenameValidity,
  stripInvalidFilenameChars,
  formatDownloadFilenameNoExt,
  formatBytes,
  formatURLFacebookShare,
  formatURLTwitterShare,
  formatURLMailto,
  initialsFromName,
  truncateText,
  getDisplayNameFromUser,
};
